import { useApi } from "@/lib/api"
import { ReminderType } from "@/models/enum"
import { Reminder, ReminderConfiguration, TemplateConfiguration, TemplateMetaTagConfiguration, UserInfo } from "@/models/interface"
import { notificationStore } from "@/store/notification-store"

const GroupName = 'ReminderNotificationCenter'
const DocumentType = 'ReminderNotification'
const FeedbackEmailAddressKeyId = 9

export const useNotificationCenterApi = () => {
    const {adminApi, publicApi} = useApi()

    const fetchReminderConfigurations = async () => {
        try{
            const response = await adminApi.get<ReminderConfiguration[]>('/globalConfiguration/ReminderNotification/ReminderNotificationCenter')
            if(response){
                const reminders = response.map((item) => item.Data)
                notificationStore.setReminders(reminders)
            }
            return response
        }catch(error){
            throw error
        }
    }

    const createReminderConfiguration = async (reminder: Reminder) => {
        const reminderConfiguration: ReminderConfiguration = {
            Data: reminder,
            GroupName,
            DocumentType
        }
        try{
            return await adminApi.post<ReminderConfiguration>('/globalConfiguration/Configuration/ReminderNotificationCenter', reminderConfiguration)

        }catch(error){
            throw error
        }
    }

    const updateReminderConfiguration = async (reminderConfiguration: ReminderConfiguration) => {
        try{
            return await adminApi.put<ReminderConfiguration>(`/globalConfiguration/Configuration/ReminderNotificationCenter`, reminderConfiguration)
        }catch(error){
            throw error
        }
    }

    const updateBulkReminderConfiguration = async (reminderConfigurations: ReminderConfiguration[]) => {
        try{
            return  await adminApi.put<ReminderConfiguration[]>('/globalConfiguration/ReminderNotification/ReminderNotificationCenter/Bulk', reminderConfigurations)
        }catch(error){
            throw error
        }
    }

    const deleteReminderConfiguration = async (reminderConfiguration: ReminderConfiguration) => {
        try{
            return await adminApi.delete<ReminderConfiguration>(`/globalConfiguration/ReminderNotification/ReminderNotificationCenter/${reminderConfiguration.id}`)
        }catch(error){
            throw error
        }
    }

    const fetchTemplateConfigurations = async (templateId?: string) => {
        try{
            const response = await adminApi.get<TemplateConfiguration[]>('/globalConfiguration/ReminderTemplate/ReminderNotificationCenter')
            if(response){
                const baseTemplate = response.find((template) => template.Data.TemplateType === ReminderType.BASE_TEMPLATE)
                
                // Filter templates based on CustomizedTemplateSourceId
                const filteredTemplates = response
                    .filter((template) => template.Data.TemplateType === ReminderType.REGULAR)
                    .filter((template) => {
                    // If templateId is provided (edit mode), include templates that either:
                    // 1. Have CustomizedTemplateSourceId === null (base templates)
                    // 2. Have CustomizedTemplateSourceId === templateId (customized versions of current template)
                    if (templateId) {
                        return template.Data.CustomizedTemplateSourceId === null ||
                               template.id === templateId
                    }
                    // If no templateId (create mode), only include templates with CustomizedTemplateSourceId === null
                    return template.Data.CustomizedTemplateSourceId === null
                })
                
                notificationStore.dispatch({
                    templates: filteredTemplates,
                    baseTemplate: baseTemplate
                })
            }
            return response
        }catch(error){
            throw error
        }
    }

    const createTemplateConfiguration = async (templateConfiguration: TemplateConfiguration) => {
        try{
            return await adminApi.post<TemplateConfiguration>('/globalConfiguration/Configuration/ReminderNotificationCenter', templateConfiguration)
        }catch(error){
            throw error
        }
    }

    const updateTemplateConfiguration = async (templateConfiguration: TemplateConfiguration) => {
        try{
            return await adminApi.put<TemplateConfiguration>(`/globalConfiguration/Configuration/ReminderNotificationCenter`, templateConfiguration)
        }catch(error){
            throw error 
        }
    }

    const fetchTemplateMetaTagConfiguration = async () => {
        try{
            const response = await adminApi.get<TemplateMetaTagConfiguration[]>('/globalConfiguration/TemplateMetaTag/ReminderNotificationCenter')
            if(response){
                notificationStore.dispatch({
                    templateMetaTags: response
                })
            }
            return response
        }catch(error){
            throw error
        }
    }

    const fetchTemplateMetaTagConfigurationById = async (id: string) => {
        try{
            return await adminApi.get<TemplateMetaTagConfiguration>(`/globalConfiguration/GetByGuid/TemplateMetaTag/${id}`)
        }catch(error){
            throw error
        }
    }

    const fetchUserInfo = async () => {
        try{
            return await publicApi.get<UserInfo>('/api/account/UserInfo')
        }catch(error){
            throw error
        }
    }

    // BusinessRuleConfiguration key 9 (FeedbackEmailAddress) for the token's tenant — the address the sent
    // reminder renders in the base template footer. Best-effort: a failure leaves the preview on the default.
    const fetchFeedbackEmailAddress = async () => {
        try{
            const response = await publicApi.get<string | null>(`/api/admin/business-rule-configuration-data/${FeedbackEmailAddressKeyId}`)
            const address = typeof response === 'string' && response.trim() !== '' ? response.trim() : null
            notificationStore.setFeedbackEmailAddress(address)
            return address
        }catch(error){
            console.warn('Could not load the tenant FeedbackEmailAddress; previews will show the default support address.', error)
            notificationStore.setFeedbackEmailAddress(null)
            return null
        }
    }

    return {
        fetchFeedbackEmailAddress,
        fetchReminderConfigurations,
        createReminderConfiguration,
        updateReminderConfiguration,
        deleteReminderConfiguration,
        updateBulkReminderConfiguration,
        fetchTemplateConfigurations,
        createTemplateConfiguration,
        updateTemplateConfiguration,
        fetchTemplateMetaTagConfiguration,
        fetchTemplateMetaTagConfigurationById,
        fetchUserInfo
    }
}